import React from 'react';

import { List, transformArrayToList } from '@common/typescript/objects/List';
import { sortByStringField } from '@common/react/utils/utils';

import { Feature } from '@commonTuna/react/objects/CompanyFeature';

import { UserRole } from '@app/objects/User';
import { CompanySettings } from '@app/objects/Company';

export interface Icon {
	type: 'image' | 'icon' | 'text';
	value: string;
	caption?: string;
}

export interface DashboardMenuItem {
	path: string;
	name: string;
	roles?: Array<UserRole>;
	features?: Array<Feature>;
	icon?: Array<string | Icon>;
	children?: List<DashboardMenuItem>;
	isFavorite?: boolean;
	anyFeature?: boolean;
	topMenu?: boolean;
}

export const dashboardMenu: Array<DashboardMenuItem> = [
	{
		path: '',
		name: 'Services',
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider],
		children: transformArrayToList([
			{
				path: '/procedure-list',
				name: 'Services',
			},
			{
				path: '/procedure-group-list',
				name: 'Service Groups',
			},
			{
				path: '/service-hours',
				name: 'Service Hours',
			},
		]),
	},
	{
		path: '',
		name: 'External Services',
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
		children: transformArrayToList([
			{
				path: 'https://www.covermymeds.com/',
				name: 'Cover My Meds',
				externalLink: true,
				features: [Feature.CoverMyMeds],
				roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
			},
			{
				path: 'https://www.labcorplink.com',
				name: 'LabCorp',
				externalLink: true,
				features: [Feature.Labcorp],
				roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
			},
			{
				path: 'https://www.questdiagnostics.com/',
				name: 'Quest Diagnostics',
				features: [Feature.Quest],
				externalLink: true,
				roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
			},
		]),
	},
	{
		path: '',
		name: 'Marketing',
		roles: [UserRole.Admin, UserRole.Doctor],
		children: transformArrayToList([
			{
				path: '/inquiries-report',
				name: 'Inquiries',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { type: 'text', value: '(x)', caption: 'Variable' }],
			},
			{
				path: '/specials-report',
				name: 'Specials',
			},
			{
				path: '/sale-list',
				name: 'Sales',
			},
			{
				path: '/subscriptions',
				name: 'Subscriptions',
			},
			{
				path: '/email-template',
				name: 'Email Templates',
			},
			{
				path: '/instagram-post-list',
				name: 'Instagram Posts',
				features: [Feature.Instagram],
			},
		]),
	},
	{
		path: '',
		name: 'Logs',
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
		children: transformArrayToList([
			{
				path: '/phone-call-list',
				name: 'Calls',
			},
			{
				path: '/patient-notifications',
				name: 'Patient Notifications',
			},
			{
				path: '/smsLogs',
				name: 'Sms',
			},
			{
				path: '/emailLogs',
				name: 'Emails',
			},
			{
				path: '/review-requests',
				name: 'Review Requests',
			},
		]),
	},
	{
		path: '',
		name: 'Admin Reports',
		roles: [UserRole.Admin, UserRole.Doctor],
		features: [Feature.PaymentsProcessing],
		children: transformArrayToList([
			{
				path: '/ledger-list',
				name: 'Billing',
				roles: [],
			},
			{
				path: '/transaction-list',
				name: 'Transactions',
			},
			{
				path: '/terminal-list',
				name: 'Terminals',
			},
			{
				path: '/billing-plan-list',
				name: 'Billing Plans',
			},
			{
				path: '/account-list',
				name: 'Chart of Accounts',
				roles: [],
			},
			{
				path: '/payment-inquiries',
				name: 'Payment Inquiries',
			},
			{
				path: '/payment-batches',
				name: 'Batches',
			},
		].sort(sortByStringField('name'))),
	},
	{
		path: '',
		name: 'Payers',
		features: [Feature.Claims],
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
		children: transformArrayToList([
			{
				path: '/payer-list',
				name: 'Payers',
			},
			{
				path: '/global-payer-company-list',
				name: 'Global Payers',
			},
		]),
	},
	{
		path: '/doctor-list',
		name: 'Staff',
	},
	{
		path: '/location-list',
		name: 'Locations',
	},
	{
		path: '',
		name: 'Medical Note Questions',
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider],
		children: transformArrayToList([
			{
				path: '/global-question-list',
				name: 'Global Questions',
			},
			{
				path: '/medical-question-list',
				name: 'Medical Questions',
			},
			{
				path: '/questionnaire-history',
				name: 'Questions History',
			},
		]),
	},
	{
		path: '',
		name: 'Settings',
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
		children: transformArrayToList([
			{
				path: '/documenttype-list',
				name: 'Document Types',
			},
			{
				path: '/appointment-type-list',
				name: 'Appointment Types',
			},
			{
				path: '/fax-rule-list',
				name: 'Fax Rules',
				features: [Feature.EFax],
			},
			{
				path: '/user-list',
				name: 'System Users',
			},
			{
				path: '/profession-list',
				name: 'Job Categories',
			},
			{
				path: '/status-list',
				name: 'Appointment Statuses',
			},
			{
				path: '/template-list',
				name: 'Consent Forms',
			},
			{
				path: '/instruction-list',
				name: 'Instruction Forms',
			},
			{
				path: '/research-forms-list',
				name: 'Research Forms',
				features: [Feature.Research],
			},
			{
				path: '/company-editor',
				name: 'Company',
			},
			{
				path: '/roleschema-list',
				name: 'Access Level Schemas',
			},
			{
				path: '/contact-list',
				name: 'Address Book',
			},
			{
				path: '/medications',
				name: 'Medications',
			},
		].sort(sortByStringField('name'))),
	},
	{
		path: '',
		name: 'WebSite Content',
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.ContentManager, UserRole.Staff],
		anyFeature: true,
		features: [Feature.Pages, Feature.Cases, Feature.Faqs, Feature.Honors, Feature.Services],
		children: transformArrayToList([
			{
				path: '/page-list',
				name: 'General Pages',
			},
			{
				path: '/case-list',
				name: 'Before-Afters',
			},
			{
				path: '/page-image-list',
				name: 'Images',
			},
			{
				path: '/faq-list',
				name: 'FAQs Page',
			},
			{
				path: '/honor-list',
				name: 'Awards Page',
			},
			{
				path: '/service-list',
				name: 'Service Pages',
			},
		].sort(sortByStringField('name'))),
	},
	{
		path: '',
		name: 'Inventory',
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
		children: transformArrayToList([
			{
				path: '/categories',
				name: 'Categories',
			},
			{
				path: '/products',
				name: 'Products',
			},
			{
				path: '/invoice-list',
				name: 'Invoices',
			},
			{
				path: '/specials-list',
				name: 'Specials',
			},
		].sort(sortByStringField('name'))),
	},
	{
		path: '',
		name: 'System',
		roles: [UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff],
		children: transformArrayToList([
			{
				path: '/document-list',
				name: 'Documents',
			},
			{
				path: '/work-shift-list',
				name: 'Work Shifts',
			},
			{
				path: '/user-session-list',
				name: 'Entry Log \xa0Journal',
			},
			{
				path: '/day-off-list',
				name: 'Calendar Events',
			},
		].sort(sortByStringField('name'))),
	},
	{
		path: '',
		name: 'Accounting',
		roles: [UserRole.Admin, UserRole.Doctor],
		children: transformArrayToList([
			{
				path: '/ledger-report',
				name: 'Ledger',
			},
			{
				path: '/order-list',
				name: 'Orders',
			},
			{
				path: '/order-details',
				name: 'Order Details',
				icon: [{ value: 'fa-search-plus ml10', caption: 'Order Details' }],
			},
			{
				path: '/quote-list',
				name: 'Quotes',
			},
			{
				path: '/refund-list',
				name: 'Refunds',
			},
			{
				path: '/revenue-report',
				name: 'Procedure/Provider',
				icon: [{ value: 'fa-stethoscope ml10', caption: 'Services' }, { value: 'fa-user-md', caption: 'Providers' }],
			},
			{
				path: '/patients-report',
				name: 'Patients Report',
				icon: [{ value: 'fa-user ml10', caption: 'Patients' }, { type: 'text', value: '(x)', caption: 'Variable' }],
			},
			{
				path: '/procedure-report',
				name: 'Procedure',
			},
			{
				path: '/product-report',
				name: 'Product',
			},
			{
				path: '/time-report',
				name: 'Apt. Duration',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { type: 'text', value: '(x)', caption: 'Variable' }],
			},
			{
				path: '/numbers-report',
				name: 'Numbers',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { type: 'text', value: '(x)', caption: 'Variable' }],
			},
			{
				path: '/payments-report',
				name: 'Payments',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { value: 'fa-usd', caption: 'Payments' }],
			},
			{
				path: '/income-report',
				name: 'Revenue Report',
				icon: [{ value: 'fa-usd ml10', caption: 'Revenue Report ' }],
			},
			{
				path: '/compare-report',
				name: 'Comparison',
				icon: [{ value: 'fa-line-chart ml10', caption: 'Chart' }],
			},
			{
				path: '/providers-report',
				name: 'Providers',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { value: 'fa-user-md', caption: 'Providers' }],
			},
			{
				path: '/source-report',
				name: 'Source',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { value: 'fa-magnet', caption: 'Source type' }],
			},
			{
				path: '/provider-report',
				name: 'Time',
				icon: [{ value: 'fa-line-chart ml10', caption: 'Chart' }],
			},
			{
				path: '/metrics-report',
				name: 'Metrics ',
				icon: [{ type: 'text', caption: 'Variable', value: ' (x)' }, { value: 'fa-user-md', caption: 'Providers' }],
			},
			{
				path: '/user-report',
				name: 'Product/Provider',
				icon: [{ value: 'fa-medkit ml10', caption: 'Products' }, { value: 'fa-user-md', caption: 'Providers' }],
			},
			{
				path: '/appointment-types-report',
				name: 'Appointment Types',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { value: 'fa-tags', caption: 'Appointment Types' }],
			},
			{
				path: '/balance-check-report',
				name: 'Balance Check',
			},
			{
				path: '/group-report',
				name: 'Sales Trends Report',
				icon: [{ value: 'fa-line-chart ml10', caption: 'Chart' }],
			},
			{
				path: '/items-report',
				name: 'Items Report',
				icon: [{ value: 'fa-object-ungroup ml10', caption: 'Items' }],
			},
			{
				path: '/dayoff-report',
				name: 'Time OFF',
				icon: [{ value: 'fa-user-md ml10', caption: 'Providers' }, { value: 'fa-calendar-times-o', caption: 'DayOff Type' }],
			},
			{
				path: '/quotas-report',
				name: 'Orders Time Report',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { value: 'fa-user-circle-o', caption: 'Users' }],
			},
			{
				path: '/quotes-report',
				name: 'Quotes Report',
				icon: [{ value: 'fa-clock-o ml10', caption: 'Time' }, { type: 'text', value: '(x)', caption: 'Variable' }],
			},
			{
				path: '/review-request-report',
				name: 'Review Request Report',
			},
			{
				path: '/inventories-report',
				name: 'Inventory Report',
			},
		].sort(sortByStringField('name'))),
	},
	{
		path: '',
		name: 'Insurances',
		children: transformArrayToList([
			{
				path: '/insurance-list',
				name: 'Claims',
			},
			{
				path: '/batch-list',
				name: 'Batches',
			},
			{
				path: '/claim-payment-documents',
				name: 'Checks',
			},
			{
				path: '/claim-reports',
				name: 'Reports',
			},
			{
				path: '/claim-payments',
				name: 'Payments',
			},
			{
				path: '/claim-payers-report',
				name: 'Payers',
			},
		]),
	},
].sort(sortByStringField('name'));

export const rootAdminMenu: Array<DashboardMenuItem> = [
	{
		path: '',
		name: 'Root',
		children: transformArrayToList([
			{
				path: '/authLink-list',
				name: 'Auth Links',
			},
			{
				path: '/region-list',
				name: 'Regions',
			},
			{
				path: '/base-hosted-service-list',
				name: 'Hosted Services',
			},
			{
				path: '/page-access-list',
				name: 'Page Accesses',
			},
			{
				path: '/portal-doctors',
				name: 'Portal Doctors',
			},
			{
				path: '/portal-specials',
				name: 'Portal Specials',
			},
			{
				path: '/serviceCall-list',
				name: 'Service Calls',
			},
			{
				path: '/system-faq-list',
				name: 'System FAQs',
			},
			{
				path: '/terms',
				name: 'Terms',
			},
			{
				path: '/user-groups',
				name: 'System User Groups',
			},
			{
				path: '/user-companies',
				name: 'User Companies',
			},
			{
				path: '/vitals',
				name: 'Vitals',
			},
			{
				path: '/sync-time-list',
				name: 'Sync Info',
			},
		]),
	},
];

export const dashboardTopMenu: Array<DashboardMenuItem & { className?: string }> = [
	{
		path: '/dashboard',
		name: 'Dashboard',
		className: 'dashboard-menu-item-mobile',
	},
	{
		path: '/calendar',
		name: 'Calendar',
		className: 'dashboard-menu-item-mobile',
	},
	{
		path: '/patient-list',
		name: 'Patients',
		className: 'dashboard-menu-item-md',
	},
	{
		path: '/appointment-list',
		name: 'Appointments',
		className: 'dashboard-menu-item-md',
	},
	{
		path: '/inquiry-list',
		name: 'Inquiries',
		className: 'dashboard-menu-item-md',
	},
	{
		path: '',
		name: 'Correspondence',
	},
	{
		path: '/task-list',
		name: 'Tasks',
	},
	{
		path: '/support-chats',
		name: 'Support Chats',
	},
	{
		path: '/verifications-list',
		name: 'Verifications',
	},
];

export const getCorrespondence = (companySettings: CompanySettings) => [
	{
		path: '/prescription-list',
		name: 'E-Rx',
		count: 0,
	},
	{
		path: companySettings.newEmailCount > 0 ? '/email-list?byInbound=true&unread=true' : '/email-list',
		name: 'Emails',
		count: companySettings.newEmailCount,
	},
	{
		path: companySettings.newFaxCount > 0 ? '/fax-list?faxEventType=1&read=1' : '/fax-list',
		name: 'Faxes',
		count: companySettings.newFaxCount,
	},
	{
		path: companySettings.newAnalysisCount > 0 ? '/analysis-list?isMatched=false' : '/analysis-list',
		name: 'Lab. Orders',
		count: companySettings.newAnalysisCount,
	},
	{
		path: companySettings.newReviewCount > 0 ? '/review-list?status=0' : '/review-list',
		name: 'Reviews',
		count: companySettings.newReviewCount,
	},
];
